<template>
  <div class="card-body p-xl-40">
    <h6 class="fs-18 mb-35 text-title fw-semibold">{{ $t("dashboard_page.recent_sales_due") }}</h6>
    <div v-if="isLoading" class="text-center py-4">
      <span>{{ $t("loading") }}</span>
    </div>
    <div v-else class="table-responsive style-one">
      <table class="table text-nowrap align-middle mb-0">
        <thead>
          <tr>
            <th scope="col" class="text-title fw-normal fs-14 pt-0 ps-0">
              {{ $t("dashboard_page.reference") }}
            </th>
            <th scope="col" class="text-title fw-normal fs-14 pt-0">
              {{ $t("dashboard_page.customer") }}
            </th>
            <th scope="col" class="text-title fw-normal fs-14 pt-0">{{ $t("dashboard_page.status") }}</th>
            <th scope="col" class="text-title fw-normal fs-14 pt-0">
              {{ $t("dashboard_page.grand_total") }}
            </th>
            <th scope="col" class="text-title fw-normal fs-14 pt-0">{{ $t("dashboard_page.paid") }}</th>
            <th scope="col" class="text-title fw-normal fs-14 pt-0">{{ $t("dashboard_page.due") }}</th>
            <th scope="col" class="text-title fw-normal fs-14 pt-0 pe-0">
              {{ $t("dashboard_page.payment_status") }}
            </th>
            <th scope="col" class="text-title fw-normal fs-14 pt-0 pe-0">
              {{ $t("common.action") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="sale in recentSales" :key="sale.id">
            <td class="shadow-none fw-normal text-black title ps-0">
              <router-link
                :to="'/sales-report'"
                class="text-light-purple text-decoration-underline fs-14 fw-semibold"
              >
                {{ sale.reference_no }}
              </router-link>
            </td>
            <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
              {{ sale.customer_name }}
            </td>
            <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
              <span 
                class="badge fs-14 fw-normal"
                :class="String(sale.status || '').toLowerCase() === 'completed' ? 'badge-success' : 'badge-danger'"
              >
                {{ formatSaleStatus(sale.status) }}
              </span>
            </td>
            <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
              ${{ Number(sale.grand_total).toFixed(2) }}
            </td>
            <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
              ${{ Number(sale.paid).toFixed(2) }}
            </td>
            <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
              ${{ Number(sale.due).toFixed(2) }}
            </td>
            <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph">
              <span 
                class="badge style-two fs-14 fw-medium"
                :class="getPaymentStatusClass(sale.payment_status)"
              >
                {{ formatPaymentStatus(sale.payment_status) }}
              </span>
            </td>
            <td class="shadow-none lh-1 fs-14 fw-normal text-paragraph pe-0">
              <button 
                class="btn btn-sm btn-primary py-1 px-2 fs-12"
                data-bs-toggle="modal" 
                data-bs-target="#paymentModal"
                @click="openPaymentModal(sale)"
              >
                {{ $t("dashboard_page.payment") }}
              </button>
            </td>
          </tr>
          <tr v-if="recentSales.length === 0">
            <td colspan="8" class="text-center py-4 text-muted">{{ $t("dashboard_page.no_recent_sales") }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Payment Modal -->
    <div class="modal fade" id="paymentModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-primary text-white border-0">
            <h5 class="modal-title fw-bold text-white">{{ $t("dashboard_page.add_payment") }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4" v-if="selectedSale">
            <div class="mb-3">
              <label class="form-label fs-14 fw-medium text-paragraph">{{ $t("dashboard_page.reference") }}</label>
              <input type="text" class="form-control bg-light border-0" :value="selectedSale.reference_no" readonly>
            </div>
            <div class="mb-3">
              <label class="form-label fs-14 fw-medium text-paragraph">{{ $t("dashboard_page.total_due") }}</label>
              <input type="text" class="form-control bg-light border-0" :value="'$' + Number(selectedSale.due).toFixed(2)" readonly>
            </div>
            <div class="mb-3">
              <label class="form-label fs-14 fw-medium text-paragraph">{{ $t("dashboard_page.payment_amount") }}</label>
              <input type="number" class="form-control h-55" v-model="paymentAmount" :max="selectedSale.due" step="0.01">
            </div>
            <div class="mb-3">
              <label class="form-label fs-14 fw-medium text-paragraph">{{ $t("dashboard_page.payment_method") }}</label>
              <select class="form-select h-55" v-model.number="paymentMethodId">
                <option :value="null">{{ $t("dashboard_page.choose_payment_method") }}</option>
                <option v-for="method in paymentMethods" :key="method.id" :value="method.id">
                  {{ method.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="modal-footer border-0 p-4 pt-0">
            <button type="button" class="btn btn-secondary h-55 px-4" data-bs-dismiss="modal">{{ $t("common.cancel") }}</button>
            <button 
              type="button" 
              class="btn btn-primary h-55 px-4" 
              @click="submitPayment"
              :disabled="isSubmittingPayment || paymentAmount <= 0 || paymentAmount > (selectedSale?.due || 0) || !paymentMethodId"
            >
              <span v-if="isSubmittingPayment" class="spinner-border spinner-border-sm me-2"></span>
              {{ $t("dashboard_page.submit_payment") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";
import type { DashboardRecentSaleItem } from "@/services/dashboard";
import salesService from "@/services/sales";
import paymentMethodService, { type PaymentMethod } from "@/services/paymentMethod";

export default defineComponent({
  name: "RecentSales",
  emits: ["payment-added"],
  props: {
    items: {
      type: Array as () => DashboardRecentSaleItem[],
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const recentSales = computed(() => props.items);
    const selectedSale = ref<DashboardRecentSaleItem | null>(null);
    const paymentMethods = ref<PaymentMethod[]>([]);
    const paymentAmount = ref(0);
    const paymentMethodId = ref<number | null>(null);
    const isSubmittingPayment = ref(false);

    const formatSaleStatus = (value?: string | null) => {
      const normalized = String(value || "pending").toLowerCase();
      const keyMap: Record<string, string> = {
        completed: "status_completed",
        pending: "status_pending",
        cancelled: "status_cancelled",
      };

      return keyMap[normalized]
        ? t(`dashboard_page.${keyMap[normalized]}`)
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const formatPaymentStatus = (value?: string | null) => {
      const normalized = String(value || "due")
        .replace(/_/g, " ")
        .toLowerCase();
      const keyMap: Record<string, string> = {
        paid: "status_paid",
        partial: "status_partial",
        due: "status_due",
        pending: "status_pending",
      };

      return keyMap[normalized]
        ? t(`dashboard_page.${keyMap[normalized]}`)
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const getPaymentStatusClass = (value?: string | null) => {
      const normalized = String(value || "due")
        .replace(/_/g, " ")
        .toLowerCase();

      return normalized === "paid" ? "badge-outline-green" : "badge-outline-red";
    };

    const loadPaymentMethods = async () => {
      try {
        paymentMethods.value = await paymentMethodService.getPaymentMethods();
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    };

    const openPaymentModal = (sale: DashboardRecentSaleItem) => {
      selectedSale.value = sale;
      paymentAmount.value = Number(sale.due);
      paymentMethodId.value = paymentMethods.value[0]?.id ?? null;
    };

    const submitPayment = async () => {
      if (!selectedSale.value) return;
      if (!paymentMethodId.value) {
        await Swal.fire({
          icon: "warning",
          title: t("dashboard_page.method_required"),
          text: t("dashboard_page.please_select_payment_method"),
        });
        return;
      }
      
      isSubmittingPayment.value = true;
      try {
        const selectedMethod = paymentMethods.value.find((method) => method.id === paymentMethodId.value);

        await salesService.addPayment(selectedSale.value.id, {
          amount: paymentAmount.value,
          paid_by: selectedMethod?.name || "Cash",
          method_id: paymentMethodId.value,
        });
        
        // Close modal
        const modalElement = document.getElementById('paymentModal');
        if (modalElement) {
          const closeButton = modalElement.querySelector('[data-bs-dismiss="modal"]') as HTMLElement;
          if (closeButton) {
            closeButton.click();
          } else {
            // Fallback if button not found
            const bootstrap = (window as any).bootstrap;
            if (bootstrap && bootstrap.Modal) {
              const modal = bootstrap.Modal.getInstance(modalElement);
              modal?.hide();
            }
          }
        }
        
        await Swal.fire({
          icon: "success",
          title: t("dashboard_page.payment_added"),
          text: t("dashboard_page.sale_payment_recorded"),
          timer: 1600,
          showConfirmButton: false,
        });
        emit("payment-added");
      } catch (error) {
        console.error("Error adding payment:", error);
        const message =
          (error as any)?.response?.data?.error ||
          (error as any)?.response?.data?.message ||
          t("dashboard_page.failed_to_add_payment");
        Swal.fire({
          icon: "error",
          title: t("dashboard_page.payment_failed"),
          text: message,
        });
      } finally {
        isSubmittingPayment.value = false;
      }
    };

    loadPaymentMethods();

    return {
      recentSales,
      selectedSale,
      paymentMethods,
      paymentAmount,
      paymentMethodId,
      isSubmittingPayment,
      formatSaleStatus,
      formatPaymentStatus,
      getPaymentStatusClass,
      openPaymentModal,
      submitPayment
    };
  },
});
</script>
